import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Loader2, Trash2, ArrowRight, Camera, ClipboardList, Theater } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1615414015111-8d98cb65677e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHx0aGVhdHJlJTIwc3RhZ2UlMjBsaWdodGluZyUyMGJhY2slMjBzdGFnZSUyMHNvdW5kYm9hcmQlMjB2ZW51ZSUyMHRlY2huaWNpYW58ZW58MHx8fHwxNzkwMjY1NTM1fDA&ixlib=rb-4.1.0&q=85";

export default function Dashboard() {
  const nav = useNavigate();
  const [surveys, setSurveys] = useState(null);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.listSurveys().then(setSurveys).catch(() => setSurveys([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      const s = await api.createSurvey({ title: title.trim() || "Plantagenet Hall Survey" });
      nav(`/survey/${s.id}`);
    } catch { toast.error("Could not create survey"); setCreating(false); }
  };

  const confirmDelete = async () => {
    const t = delTarget; setDelTarget(null);
    try { await api.deleteSurvey(t.id); setSurveys((s) => s.filter((x) => x.id !== t.id)); toast.success("Survey deleted"); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="relative z-10 min-h-screen">
      {/* Hero */}
      <header className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(11,15,23,0.75) 0%, rgba(11,15,23,0.92) 100%)" }} />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
          <div className="mb-4 flex items-center gap-2">
            <Theater className="h-5 w-5 text-primary" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Field Workbook · Venue Technical Reference</span>
          </div>
          <h1 className="max-w-3xl font-mono text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Plantagenet Hall<br />Technical Site Survey
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Photograph, measure and inventory the entire venue as-built — then export a print-ready technical reference. Optimised for one-handed use on-site.
          </p>
          <div className="mt-7 flex flex-wrap gap-6">
            {[["Camera", "Guided photo register"], ["ClipboardList", "28 sections, 6 parts"]].map(([_, label], i) => (
              <div key={label} className="flex items-center gap-2 text-xs text-muted-foreground">
                {i === 0 ? <Camera className="h-4 w-4 text-primary" /> : <ClipboardList className="h-4 w-4 text-primary" />}
                {label}
              </div>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h2 className="text-xl font-bold tracking-tight sm:text-2xl">Surveys</h2>
          <Button data-testid="new-survey-btn" onClick={() => { setTitle(""); setOpen(true); }} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
            <Plus className="h-4 w-4" /> New survey
          </Button>
        </div>

        {surveys === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : surveys.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border py-16 text-center">
            <ClipboardList className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No surveys yet. Start your first site walk-through.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {surveys.map((s) => (
              <div key={s.id} data-testid={`survey-card-${s.id}`} className="group flex flex-col rounded-2xl border border-border bg-card p-5 card-hover">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-base font-semibold leading-tight text-foreground">{s.title}</h3>
                  <Button data-testid={`survey-del-${s.id}`} variant="ghost" size="icon" className="-mr-2 -mt-1 h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => setDelTarget(s)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                <div className="mt-3 flex items-center gap-4 font-mono text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Camera className="h-3.5 w-3.5" /> {s.photo_count} {s.photo_count === 1 ? "photo" : "photos"}</span>
                  <span>{new Date(s.updated_at).toLocaleDateString()}</span>
                </div>
                <Button data-testid={`survey-open-${s.id}`} onClick={() => nav(`/survey/${s.id}`)} variant="outline" className="mt-4 justify-between gap-2 tap-scale hover:border-primary/50">
                  Open workbook <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New site survey</DialogTitle>
            <DialogDescription>Give this walk-through a name to start capturing photos and measurements.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Input data-testid="new-survey-title" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Plantagenet Hall Survey" className="bg-secondary/40" onKeyDown={(e) => e.key === "Enter" && create()} />
            <Button data-testid="create-survey-confirm" onClick={create} disabled={creating} className="w-full gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create & start
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this survey?</AlertDialogTitle>
            <AlertDialogDescription>“{delTarget?.title}” and its photos will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-survey" onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
